import React, { useEffect, useState } from 'react';

import { LineChart, CartesianGrid, XAxis, YAxis, Tooltip, Line, ResponsiveContainer } from 'recharts';

import './Dashboard.css';

export const DataCaptureDashboard = ({ dashboard, renderDuration }) => {
  const [studies, setStudies] = useState([]);

  useEffect(() => {
    if (dashboard) {
      const studiesData = [...(dashboard.dataManagementDashboardVOS || [])];

      studiesData.sort((a, b) => {
        if (b.overdueSDVCaptures !== a.overdueSDVCaptures) {
          return b.overdueSDVCaptures - a.overdueSDVCaptures;
        }

        if (b.overdueDataCaptures !== a.overdueDataCaptures) {
          return b.overdueDataCaptures - a.overdueDataCaptures;
        }

        if (b.pendingDataCaptures !== a.pendingDataCaptures) {
          return b.pendingDataCaptures - a.pendingDataCaptures;
        }

        if (b.pendingSDVCaptures !== a.pendingSDVCaptures) {
          return b.pendingSDVCaptures - a.pendingSDVCaptures;
        }

        return 0;
      });

      setStudies(studiesData);
    }
  }, [dashboard]);

  const parseDurationDates = (duration) => {
    if (!duration) {
      return {
        startDate: null,
        endDate: null,
      };
    }

    const [startPart, endPart] = duration.split(' - ');

    return {
      startDate: startPart?.split(' ')[0] || null,
      endDate: endPart?.split(' ')[0] || null,
    };
  };

  const mergeData = (pendingDataCapturesData = [], pendingSDVCapturesData = [], duration) => {
    const { startDate, endDate } = parseDurationDates(duration);
    const mergedData = {};

    if (startDate) {
      mergedData[startDate] = {
        ...mergedData[startDate],
        date: startDate,
      };
    }

    if (endDate) {
      mergedData[endDate] = {
        ...mergedData[endDate],
        date: endDate,
      };
    }

    pendingDataCapturesData.forEach((item) => {
      const { date, count } = item;

      mergedData[date] = {
        ...mergedData[date],
        date,
        pendingDataCaptures: count,
      };
    });

    pendingSDVCapturesData.forEach((item) => {
      const { date, count } = item;

      mergedData[date] = {
        ...mergedData[date],
        date,
        pendingSDVCaptures: count,
      };
    });

    return Object.values(mergedData).sort((a, b) => new Date(a.date) - new Date(b.date));
  };

  return (
    <div className="dashboard">
      <div className="dashboard-section">
        {studies.map((study, index) => {
          const mergedData = mergeData(study.pendingDataCaptureData, study.pendingSDVCaptureData, study.duration);

          const { startDate, endDate } = parseDurationDates(study.duration);

          const xAxisTicks = [startDate, endDate].filter(Boolean);

          const showRedBackground = study.overdueDataCaptures > 0 || study.overdueSDVCaptures > 0;

          const showRedBorder = study.pendingDataCaptures > 0 || study.pendingSDVCaptures > 0 || showRedBackground;

          return (
            <div
              key={index}
              className={`dashboard-container 
                ${showRedBorder ? 'red-border' : 'green-border'}
                ${showRedBackground ? 'red-background' : ''}`}
            >
              <label className="dashboard-header-label">{study.study}</label>

              <div className="section-big">
                <div className="section-left">
                  <label className="dashboard-label">Last Updated: {study.lastUpdated ?? '-'}</label>

                  <label className="dashboard-label">Duration: {renderDuration(study.duration)}</label>

                  <label className="dashboard-label">Site: {study.site ?? '-'}</label>
                </div>

                <div className="section-left">
                  <label className="dashboard-label">Total Participants: {study.participants ?? '0'}</label>

                  <label className="dashboard-label">In-Screening: {study.inScreeningParticipants ?? '0'}</label>

                  <label className="dashboard-label">Screen Failure: {study.screenFailureParticipants ?? '0'}</label>

                  <label className="dashboard-label">Enrolled: {study.enrolledParticipants ?? '0'}</label>

                  <label className="dashboard-label">Terminated: {study.terminatedParticipants ?? '0'}</label>
                </div>

                <div className="section-right">
                  <label className="dashboard-label">Successful Visits: {study.successfulVisits ?? '0'}</label>

                  <label className="dashboard-label">Unscheduled Visits: {study.unscheduledVisits ?? '0'}</label>

                  <label className="dashboard-label">Out-of-Window Visits: {study.outOfWindowVisits ?? '0'}</label>

                  <label className="dashboard-label">Missed Visits: {study.missedVisits ?? '0'}</label>

                  <label className="dashboard-label">Uncaptured Visits: {study.uncapturedVisits ?? '0'}</label>
                </div>
              </div>

              <div className="section-big">
                <div className="section-left" style={{ flex: 1 }} />

                <div className="section-mid">
                  <ResponsiveContainer height={150} width="95%" debounce={50}>
                    <LineChart
                      data={mergedData}
                      margin={{
                        top: 10,
                        right: 40,
                        bottom: 5,
                        left: 15,
                      }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />

                      <XAxis dataKey="date" type="category" ticks={xAxisTicks} interval={0} padding={{ right: 10 }} />

                      <YAxis width={45} tickMargin={10} />

                      <Tooltip />

                      <Line type="monotone" dataKey="pendingDataCaptures" stroke="#FF0000" />

                      <Line type="monotone" dataKey="pendingSDVCaptures" stroke="#077187" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>

                <div className="section-right" style={{ flex: 1 }}>
                  <label
                    className={`dashboard-label ${
                      study.pendingDataCaptures > 0 || study.pendingSDVCaptures > 0 ? 'red-label' : ''
                    } red-graph-data`}
                  >
                    Pending Data Captures: {study.pendingDataCaptures}
                  </label>

                  <label
                    className={`dashboard-label ${
                      study.overdueDataCaptures > 0 || study.overdueSDVCaptures > 0 ? 'red-label' : ''
                    } normal`}
                  >
                    Overdue Data Captures: {study.overdueDataCaptures}
                  </label>

                  <label className="dashboard-label blue-label blue-graph-data">
                    Pending SDV Captures: {study.pendingSDVCaptures}
                  </label>

                  <label
                    className={`dashboard-label ${
                      study.overdueDataCaptures > 0 || study.overdueSDVCaptures > 0 ? 'red-label' : ''
                    } normal`}
                  >
                    Overdue SDV Captures: {study.overdueSDVCaptures}
                  </label>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
